import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff, LogIn } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login, isAuthenticated, loading } = useAuth();

  useEffect(() => {
    // Only redirect if auth check is complete AND user is authenticated
    if (!loading && isAuthenticated) {
      navigate('/dashboard');
    }

    // Check for token in URL params (from Google OAuth)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      // Store token and redirect
      localStorage.setItem('token', token);
      window.location.href = '/dashboard';
    }
  }, [isAuthenticated, loading, navigate]);

  const handleGoogleLogin = async () => {
    try {
      // Redirect to Google OAuth
      const { getApiBaseUrlWithoutPath } = await import('../config');
      const baseUrl = getApiBaseUrlWithoutPath();
      window.location.href = `${baseUrl}/auth/google`;
    } catch (error) {
      console.error('Google login failed:', error);
      toast({
        title: "Login Failed",
        description: "Google login is not available at the moment.",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Login attempt:', { email, password });
    setIsLoading(true);

    try {
      const response = await login({ email, password });
      console.log('Login response:', response);
      
      if (response.success) {
        console.log('Login successful');
        toast({
          title: "Login Successful",
          description: "Welcome back!",
        });
        
        // Redirect based on user role - roles is an array
        const userRoles = response.data.user.roles || [];
        const isAdmin = userRoles.includes('admin');
        const isProvider = userRoles.includes('provider');
        
        if (isAdmin) {
          navigate('/admin/dashboard');
        } else if (isProvider) {
          navigate('/provider/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        console.log('Login failed:', response.message);
        toast({
          title: "Login Failed",
          description: response.message || "Invalid credentials",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Login Failed",
        description: error.message || "An error occurred during login",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Login | Virewire Property Services</title>
      </Helmet>

      <section className="section-padding min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="container-custom max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <LogIn className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">Sign In</h1>
            <p className="text-muted-foreground">
              Access your Virewire account
            </p>
          </div>

          <div className="bg-card rounded-2xl p-8 shadow-card">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    className="pl-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link 
                  to="/register" 
                  className="text-primary font-semibold hover:underline"
                >
                  Sign up here
                </Link>
              </div>
            </form>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LoginPage;